"use client";

import { useInView } from "framer-motion";
import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { Cell, Pie, PieChart, ResponsiveContainer } from "recharts";

type ChartData = {
  name: string;
  value: number;
  color: string;
  [key: string]: string | number;
};

type DonutChartProps = {
  data: ChartData[];
  highlightSegments: string[];
  centerText: ReactNode;
  className?: string;
};

// Helper function to get lighter color with opacity
const getLighterColor = (color: string): string => {
  // If it's a CSS custom property, return it with opacity
  if (color.startsWith("var(--")) {
    return `color-mix(in srgb, ${color} 50%, transparent)`;
  }

  // For other color formats, return as is
  return color;
};

export function DonutChart({
  data,
  highlightSegments,
  centerText,
  className = "relative h-80 md:h-94",
}: DonutChartProps) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });

  // State for responsive radius values
  const [radius, setRadius] = useState({
    innerRadius: 120,
    outerRadius: 165,
    highlightInnerRadius: 168,
    highlightOuterRadius: 179,
  });

  // Update radius based on screen size
  useEffect(() => {
    const updateRadius = () => {
      const isMobile = window.innerWidth < 768;
      setRadius({
        innerRadius: isMobile ? 80 : 120,
        outerRadius: isMobile ? 110 : 165,
        highlightInnerRadius: isMobile ? 112 : 168,
        highlightOuterRadius: isMobile ? 120 : 179,
      });
    };

    // Set initial radius
    updateRadius();

    // Add resize listener
    window.addEventListener("resize", updateRadius);

    // Cleanup
    return () => window.removeEventListener("resize", updateRadius);
  }, []);

  return (
    <div ref={ref} className={className}>
      {isInView && (
        <>
          <ResponsiveContainer
            initialDimension={{ width: 320, height: 200 }}
            width="100%"
            height="100%"
            aspect={1}
          >
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius={radius.innerRadius}
                outerRadius={radius.outerRadius}
                paddingAngle={2}
                dataKey="value"
                animationBegin={0}
                animationDuration={1500}
              >
                {data.map((entry) => (
                  <Cell key={entry.name} fill={entry.color} />
                ))}
              </Pie>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius={radius.highlightInnerRadius}
                outerRadius={radius.highlightOuterRadius}
                paddingAngle={2}
                dataKey="value"
                animationBegin={0}
                animationDuration={1500}
              >
                {data.map((entry) => (
                  <Cell
                    key={`highlight-${entry.name}`}
                    fill={
                      highlightSegments.includes(entry.name)
                        ? getLighterColor(entry.color)
                        : "transparent"
                    }
                  />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>

          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <p className="text-gray-900 text-sm w-2/3 mx-auto md:text-base md:w-full">
                {centerText}
              </p>
            </div>
          </div>

          {/* Color legend under the chart */}
          <div className="flex justify-center space-x-4 mt-2 md:mt-4">
            {data.map((entry) => (
              <div
                key={`label-${entry.name}`}
                className="flex items-center space-x-1"
              >
                <div
                  className="w-5 h-3"
                  style={{ backgroundColor: entry.color }}
                />
                <span className="text-xs font-semibold text-gray-600">
                  {entry.name}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
